import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Save, RotateCcw, Lock, Plus, Trash2, X, Flame } from 'lucide-react';

const emptyEnergieForm = { code: '', libelle: '', tarif: '', unite: '€/MWh', ordre: 99 };

const AdminTarifsFiscauxEnergies = () => {
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);
  const [draft, setDraft] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newE, setNewE] = useState(emptyEnergieForm);
  const [creating, setCreating] = useState(false);

  const canEdit = hasPermission && hasPermission('admin_tarifs', 'edit');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/tarifs-fiscaux-energies');
      setData(data);
      const d = {};
      (data.items || []).forEach((e) => {
        d[e.code] = { libelle: e.libelle, tarif: e.tarif, unite: e.unite, ordre: e.ordre };
      });
      setDraft(d);
    } catch (e) {
      toast.error('Erreur chargement des tarifs fiscaux');
    } finally {
      setLoading(false);
    }
  };

  const isDirty = (code) => {
    const e = (data?.items || []).find((x) => x.code === code);
    const d = draft[code];
    if (!e || !d) return false;
    return (
      String(d.libelle ?? '') !== String(e.libelle ?? '') ||
      Number(d.tarif) !== Number(e.tarif) ||
      String(d.unite ?? '') !== String(e.unite ?? '') ||
      Number(d.ordre) !== Number(e.ordre)
    );
  };

  const handleSave = async (code) => {
    const d = draft[code];
    if (!d?.libelle?.trim()) { toast.error('Libellé obligatoire'); return; }
    const tarif = Number(d.tarif);
    if (Number.isNaN(tarif) || tarif < 0) { toast.error('Tarif invalide (≥ 0)'); return; }
    setSavingCode(code);
    try {
      const { data: result } = await api.put(`/admin/tarifs-fiscaux-energies/${code}`, {
        libelle: d.libelle.trim(),
        tarif,
        unite: d.unite || '€/MWh',
        ordre: Number(d.ordre) || 99,
      });
      setData(result);
      toast.success(`Énergie ${code} mise à jour`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSavingCode(null);
    }
  };

  const handleDelete = async (code) => {
    if (!confirm(`Supprimer définitivement l'énergie ${code} ?`)) return;
    try {
      const { data: result } = await api.delete(`/admin/tarifs-fiscaux-energies/${code}`);
      setData(result);
      toast.success(`Énergie ${code} supprimée`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleCreate = async () => {
    if (!newE.code.trim()) { toast.error('Code obligatoire'); return; }
    if (!newE.libelle.trim()) { toast.error('Libellé obligatoire'); return; }
    const tarif = Number(newE.tarif);
    if (Number.isNaN(tarif) || tarif < 0) { toast.error('Tarif invalide'); return; }
    setCreating(true);
    try {
      await api.post('/admin/tarifs-fiscaux-energies', {
        code: newE.code.trim().toUpperCase(),
        libelle: newE.libelle.trim(),
        tarif,
        unite: newE.unite || '€/MWh',
        ordre: Number(newE.ordre) || 99,
      });
      toast.success('Énergie créée');
      setShowAddModal(false);
      setNewE(emptyEnergieForm);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setCreating(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Réinitialiser tous les tarifs fiscaux aux valeurs par défaut 2025 ?')) return;
    try {
      const { data: result } = await api.post('/admin/tarifs-fiscaux-energies/reset');
      setData(result);
      toast.success('Tarifs réinitialisés');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur réinitialisation');
    }
  };

  if (loading) return <div className="p-6 text-sm text-[#687175]">Chargement…</div>;

  return (
    <div className="p-6" data-testid="admin-tarifs-fiscaux">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#191D1E] flex items-center gap-2">
            <Flame className="w-6 h-6 text-orange-500" /> Tarifs fiscaux par catégorie d'énergie
          </h1>
          <p className="text-sm text-[#687175] mt-1">
            Utilisés pour le calcul du ratio Valeur de Production TICGN :
            <strong> Ratio = Σ (Conso × Tarif fiscal) / VP &gt; 3 %</strong>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!canEdit && (
            <span className="text-xs text-amber-700 inline-flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Lecture seule</span>
          )}
          {canEdit && (
            <>
              <button onClick={() => setShowAddModal(true)}
                className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm inline-flex items-center gap-1"
                data-testid="add-energie-btn">
                <Plus className="w-4 h-4" /> Ajouter une énergie
              </button>
              <button onClick={handleReset}
                className="px-3 py-2 bg-white border border-[#BFC6CC] text-[#687175] hover:bg-gray-50 rounded-lg text-sm inline-flex items-center gap-1"
                data-testid="reset-btn">
                <RotateCcw className="w-4 h-4" /> Réinitialiser
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#BFC6CC] overflow-hidden">
        <table className="w-full text-sm" data-testid="energies-table">
          <thead className="bg-gray-50 text-xs text-[#687175] uppercase">
            <tr>
              <th className="text-left px-4 py-2.5">Code</th>
              <th className="text-left px-4 py-2.5">Libellé</th>
              <th className="text-right px-4 py-2.5 w-32">Tarif</th>
              <th className="text-left px-4 py-2.5 w-24">Unité</th>
              <th className="text-right px-4 py-2.5 w-20">Ordre</th>
              <th className="text-right px-4 py-2.5 w-44">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).map((e) => {
              const d = draft[e.code] || {};
              const dirty = isDirty(e.code);
              return (
                <tr key={e.code} className="border-t border-gray-100" data-testid={`row-${e.code}`}>
                  <td className="px-4 py-2 font-mono text-xs text-[#191D1E]">{e.code}</td>
                  <td className="px-4 py-2">
                    <input type="text" value={d.libelle ?? ''}
                      onChange={(ev) => setDraft({ ...draft, [e.code]: { ...d, libelle: ev.target.value } })}
                      disabled={!canEdit}
                      className="w-full px-2 py-1 border border-[#BFC6CC] rounded text-sm disabled:bg-gray-50"
                      data-testid={`libelle-${e.code}`} />
                  </td>
                  <td className="px-4 py-2">
                    <input type="number" step="0.001" min="0" value={d.tarif ?? ''}
                      onChange={(ev) => setDraft({ ...draft, [e.code]: { ...d, tarif: ev.target.value } })}
                      disabled={!canEdit}
                      className="w-full px-2 py-1 border border-[#BFC6CC] rounded text-sm text-right disabled:bg-gray-50"
                      data-testid={`tarif-${e.code}`} />
                  </td>
                  <td className="px-4 py-2">
                    <input type="text" value={d.unite ?? ''}
                      onChange={(ev) => setDraft({ ...draft, [e.code]: { ...d, unite: ev.target.value } })}
                      disabled={!canEdit}
                      className="w-full px-2 py-1 border border-[#BFC6CC] rounded text-sm disabled:bg-gray-50"
                      data-testid={`unite-${e.code}`} />
                  </td>
                  <td className="px-4 py-2">
                    <input type="number" value={d.ordre ?? ''}
                      onChange={(ev) => setDraft({ ...draft, [e.code]: { ...d, ordre: ev.target.value } })}
                      disabled={!canEdit}
                      className="w-full px-2 py-1 border border-[#BFC6CC] rounded text-sm text-right disabled:bg-gray-50"
                      data-testid={`ordre-${e.code}`} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canEdit && (
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => handleSave(e.code)}
                          disabled={!dirty || savingCode === e.code}
                          className="px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-xs inline-flex items-center gap-1 disabled:opacity-40"
                          data-testid={`save-${e.code}`}>
                          <Save className="w-3 h-3" /> {savingCode === e.code ? '...' : 'Enregistrer'}
                        </button>
                        <button onClick={() => handleDelete(e.code)}
                          className="px-2 py-1 bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded text-xs inline-flex items-center gap-1"
                          data-testid={`delete-${e.code}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(data?.items || []).length === 0 && (
          <div className="p-8 text-center text-sm text-[#687175]">Aucune énergie configurée.</div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-[#BFC6CC] p-6 w-full max-w-md" data-testid="add-energie-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Nouvelle catégorie fiscale</h2>
              <button onClick={() => setShowAddModal(false)} className="text-[#687175] hover:text-[#191D1E]"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-[#687175] mb-1">Code (uppercase)</label>
                <input type="text" value={newE.code} onChange={(e) => setNewE({ ...newE, code: e.target.value.toUpperCase() })}
                  placeholder="BIOMASSE" className="w-full px-3 py-2 border border-[#BFC6CC] rounded text-sm" data-testid="new-code" />
              </div>
              <div>
                <label className="block text-xs text-[#687175] mb-1">Libellé</label>
                <input type="text" value={newE.libelle} onChange={(e) => setNewE({ ...newE, libelle: e.target.value })}
                  className="w-full px-3 py-2 border border-[#BFC6CC] rounded text-sm" data-testid="new-libelle" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-[#687175] mb-1">Tarif</label>
                  <input type="number" step="0.001" min="0" value={newE.tarif} onChange={(e) => setNewE({ ...newE, tarif: e.target.value })}
                    className="w-full px-3 py-2 border border-[#BFC6CC] rounded text-sm" data-testid="new-tarif" />
                </div>
                <div>
                  <label className="block text-xs text-[#687175] mb-1">Unité</label>
                  <input type="text" value={newE.unite} onChange={(e) => setNewE({ ...newE, unite: e.target.value })}
                    className="w-full px-3 py-2 border border-[#BFC6CC] rounded text-sm" data-testid="new-unite" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#687175] mb-1">Ordre d'affichage</label>
                <input type="number" value={newE.ordre} onChange={(e) => setNewE({ ...newE, ordre: e.target.value })}
                  className="w-full px-3 py-2 border border-[#BFC6CC] rounded text-sm" data-testid="new-ordre" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAddModal(false)} className="px-3 py-2 border border-[#BFC6CC] rounded text-sm">Annuler</button>
              <button onClick={handleCreate} disabled={creating}
                className="px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="create-btn">
                <Plus className="w-4 h-4" /> {creating ? 'Création…' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTarifsFiscauxEnergies;
